import { flipCoin, getRandomPositiveInt } from './mathUtils';

export function groupByAndGetValues<T>(items: T[], byKey: keyof T): Array<T[]> {
	const itemsByValues: Record<string, T[]> = {};
	for (const item of items) {
		const value = item[byKey] as unknown as string;
		itemsByValues[value] ||= [];
		itemsByValues[value].push(item);
	}
	return Object.values(itemsByValues);
}

export function groupByItem<T>(
	items: T[],
	mapperFn: (item: T) => string | number
): Record<string, T[]> {
	const itemsByValues: Record<string, T[]> = {};
	for (const item of items) {
		const key = mapperFn(item);
		itemsByValues[key] ||= [];
		itemsByValues[key].push(item);
	}
	return itemsByValues;
}

export function groupBy<T>(items: T[], byKey: keyof T): Array<T[]> {
	const itemsByValues: Record<string, T[]> = {};
	for (const item of items) {
		const value = item[byKey] as unknown as string;
		itemsByValues[value] ||= [];
		itemsByValues[value].push(item);
	}
	return Object.values(itemsByValues);
}

export function toMapObject<T>(
	items: T[],
	byKey: keyof T | ((item: T) => string)
): Record<string, T> {
	const itemsByValues: Record<string | number, T> = {};
	for (const item of items) {
		let keyObject;
		if (typeof byKey === 'function') {
			keyObject = byKey(item);
		} else {
			keyObject = item[byKey] as unknown as string | number;
		}
		itemsByValues[keyObject] = item;
	}
	return itemsByValues;
}

export function removeDuplicates<T>(
	items: T[],
	propertyOrFn: keyof T | ((item: T) => string | number)
): T[] {
	const seenItems = new Set();
	const result: T[] = [];
	items.forEach((item) => {
		let value: string;
		if (typeof propertyOrFn == 'function') {
			value = propertyOrFn(item) as string;
		} else {
			value = item[propertyOrFn] as string;
		}
		if (!seenItems.has(value)) {
			result.push(item);
			seenItems.add(value);
		}
	});
	return result;
}

export function removeDuplicatesPrimitive<T = number | boolean | string>(items: T[]): T[] {
	return Array.from(new Set(items));
}

export function pushAll<T>(array: T[], newItems: T[]): T[] {
	newItems.forEach((item) => array.push(item));
	return array;
}

export function addAllToSet<T>(set: Set<T>, newElements: T[]): void {
	newElements.forEach((element) => set.add(element));
}

export function add<T>(set: Set<T>, item: T): Set<T> {
	set.add(item);
	return set;
}

export function toggleSetItem<T>(set: Set<T>, item: T): Set<T> {
	if (set.has(item)) {
		set.delete(item);
	} else {
		set.add(item);
	}
	return set;
}

export function includesAny<T>(array: T[], ...candidates: T[]) {
	if (!candidates) {
		return false;
	}
	return array.some((item) => candidates.includes(item));
}

export function removeIndex<T>(array: T[], index: number): T[] {
	array.splice(index, 1);
	return array;
}

export function deleteFromSet<T>(set: Set<T>, item: T): Set<T> {
	set.delete(item);
	return set;
}

export function isEmpty<T>(array: T[] | null | undefined): boolean {
	return array == null || array.length === 0;
}

export function isEmptyObject(obj: object | null | undefined): obj is null {
	if (obj == null) {
		return true;
	}
	for (const _ in obj) {
		return false;
	}
	return true;
}

export function isNotEmpty<T>(array: T[] | null | undefined): boolean {
	return array != null && array.length > 0;
}

export function firstOrNull<T>(array: T[]): T | null {
	if (array?.length > 0) {
		return array[0];
	}
	return null;
}

export function getLast<T>(items: T[]): T {
	return items[items.length - 1];
}

export function lastOrNull<T>(array: T[]): T | null {
	if (array?.length > 0) {
		return array[array.length - 1];
	}
	return null;
}

export function randomPick<T>(...candidates: T[]): T {
	return candidates[getRandomPositiveInt(candidates.length)]!;
}

export function randomPickOrNull<T>(array: T[]): T | null {
	if (array.length == 0) {
		return null;
	}
	return array[getRandomPositiveInt(array.length)];
}

export function getConsecutiveNumberArray(start: number, end: number): number[] {
	return [...Array(end - start).keys()].map((i) => i + start);
}

export function partition<T>(items: T[], size: number) {
	const output: Array<T[]> = [];
	for (let i = 0; i < items.length; i += size) {
		output[output.length] = items.slice(i, i + size);
	}
	return output;
}

export function joinWithDifferentLastSeparator(
	items: string[],
	separator: string,
	separatorBeforeLast: string
) {
	if (items.length == 1) {
		return items[0];
	}
	return items.slice(0, -1).join(separator) + separatorBeforeLast + items.slice(-1);
}

export function sum(items: number[]) {
	return items.reduce((previous, current) => previous + current, 0);
}

export function shuffle<T>(items: T[]) {
	items.sort(() => (flipCoin() ? 0 : 1));
	return items;
}

export function emptyIfNull<T>(items: T[]) {
	return items ?? [];
}

export function keysAsInts(obj: object): number[] {
	return Object.keys(obj).map((key) => parseInt(key, 10));
}

export function containsExactly<T>(array: T[], items: T[]) {
	return array.length === items.length && array.every((item) => items.includes(item));
}

export function containsExactlyBy<T>(array: T[], items: T[], idFn: (item: T) => string) {
	if (array.length !== items.length) {
		return false;
	}
	return containsExactly(
		array.map((item) => idFn(item)),
		items.map((item) => idFn(item))
	);
}

export function getOnlyElement<T>(array: T[] | null): T | null {
	if (array?.length == 1) {
		return array[0];
	}
	return null;
}

export function toggleElement<T = number | string>(array: T[], item: T) {
	const index = array.indexOf(item);
	if (index === -1) {
		array.push(item);
		return array;
	}
	return removeIndex(array, index);
}

export function toggleMapElement<T = number | string, V = object>(
	map: Map<T, V>,
	key: T,
	value: V
) {
	if (map.has(key)) {
		map.delete(key);
	} else {
		map.set(key, value);
	}
	return map;
}

export function betweenIndexes<T>(
	items: T[],
	startIndexInclusive: number,
	endIndexExclusive: number
) {
	return [...items].splice(startIndexInclusive, endIndexExclusive - startIndexInclusive);
}

export function isLastIndex<T>(index: number, items: null | undefined | T[]) {
	if (!items) {
		return true;
	}
	return index === items.length - 1;
}

export function keyCount(obj: object) {
	return Object.keys(obj).length;
}

export function mapToObject<K extends string | number | symbol, V>(obj: Map<K, V>): Record<K, V> {
	const result: Record<K, V> = {} as Record<K, V>;
	Array.from(obj.entries()).forEach(
		(key, value) =>
			// @ts-expect-error we assume key is a valid key here
			(result[key] = value)
	);
	return result;
}
